import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NotificationsService } from 'angular2-notifications';
import { FormBuilder, Validators } from '@angular/forms';
import { OfframpService } from '../../services/offramp.service';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import * as moment from 'moment';

const defaults = {
  notif: {
    timeOut: 3000,
    showProgressBar: true,
    pauseOnHover: false,
    clickToClose: true,
    maxLength: 50,
  },
};

// Adding a new row is for a transfer the on-chain auto-detection missed -
// every field is editable then. Editing an existing row only ever touches
// matchedTopupAmount/matchedStatus (everything else stays disabled, since
// it's meant to mirror what was auto-detected on-chain).
@Component({
  selector: 'app-add-offramp-crypto-received',
  templateUrl: './add-offramp-crypto-received.component.html',
})
export class AddOfframpCryptoReceivedDialog implements OnInit {
  element: any = {};
  myForm: any;
  isEditMode: boolean = false;
  recordId: string = '';
  today: Date = new Date();

  customerList: any[] = [];
  filteredCustomerList: any[] = [];
  userSearchValue: string = '';
  isUserSelectOpen: boolean = false;

  networkOptions = ['TRC20', 'SOLANA'];
  currencyOptions = ['USDT', 'USDC'];
  matchedStatusOptions: string[] = ['PENDING', 'PARTIAL', 'COMPLETED'];

  constructor(
    public dialogRef: MatDialogRef<AddOfframpCryptoReceivedDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private notifSer: NotificationsService,
    public fb: FormBuilder,
    public offrampService: OfframpService,
    private spinnerService: Ng4LoadingSpinnerService
  ) {
    this.element = data.record || {};
    this.isEditMode = !!this.element._id;
    this.recordId = this.element._id || '';

    this.myForm = this.fb.group({
      userId: [
        {
          value: this.element.userId ? (this.element.userId._id || this.element.userId) : '',
          disabled: this.isEditMode
        },
        [Validators.required]
      ],
      network: [{ value: this.element.network || 'TRC20', disabled: this.isEditMode }, [Validators.required]],
      currency: [{ value: this.element.currency || 'USDT', disabled: this.isEditMode }, [Validators.required]],
      fromAddress: [{ value: this.element.fromAddress || '', disabled: this.isEditMode }, [Validators.required]],
      toAddress: [{ value: this.element.toAddress || '', disabled: this.isEditMode }, [Validators.required]],
      amount: [{ value: this.element.amount || 0, disabled: this.isEditMode }, [Validators.required, Validators.min(0.000001)]],
      txHash: [{ value: this.element.txHash || '', disabled: this.isEditMode }, [Validators.required]],
      txDate: [{ value: this.element.txDate ? new Date(this.element.txDate) : new Date(), disabled: this.isEditMode }, [Validators.required]],
      matchedTopupAmount: [this.element.matchedTopupAmount || 0, [Validators.required, Validators.min(0)]],
      matchedStatus: [this.element.matchedStatus || 'PENDING', [Validators.required]],
    });
  }

  ngOnInit() {
    if (!this.isEditMode) {
      this.loadCustomerList();
    }
  }

  loadCustomerList() {
    this.spinnerService.show();
    const filter = {
      _csrf: this.data._csrf,
      perpage: 10000,
      page: 1,
      search: '',
      status: 'Active',
    };

    this.offrampService.getOfframpCustomerList(filter).subscribe(
      (response: any) => {
        this.spinnerService.hide();
        if (response && response.customers && Array.isArray(response.customers)) {
          this.customerList = response.customers;
        } else if (response && Array.isArray(response)) {
          this.customerList = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          this.customerList = response.data;
        } else {
          this.customerList = [];
        }
        this.filteredCustomerList = this.customerList;
      },
      () => {
        this.spinnerService.hide();
        this.notifSer.error('Error', 'Failed to load customer list', defaults.notif);
        this.customerList = [];
        this.filteredCustomerList = [];
      }
    );
  }

  onUserSelectOpened(isOpen: boolean) {
    this.isUserSelectOpen = isOpen;
    if (isOpen) {
      this.userSearchValue = '';
      this.filteredCustomerList = this.customerList;
      setTimeout(() => {
        this.addSearchToSelectPanel();
      }, 150);
    } else {
      this.userSearchValue = '';
      this.filteredCustomerList = this.customerList;
    }
  }

  addSearchToSelectPanel() {
    const panel = document.querySelector('.user-select-panel-crypto-received');
    if (panel) {
      const existingSearch = panel.querySelector('.user-search-container');
      if (existingSearch) {
        existingSearch.remove();
      }

      const searchContainer = document.createElement('div');
      searchContainer.className = 'user-search-container';
      searchContainer.innerHTML = `
        <input type="text" placeholder="Search user..." class="user-search-input" value="${this.userSearchValue || ''}">
      `;

      const searchInput = searchContainer.querySelector('.user-search-input') as HTMLInputElement;
      if (searchInput) {
        searchInput.addEventListener('input', (e: any) => {
          this.userSearchValue = e.target.value;
          this.filterUsers(e);
        });
        searchInput.addEventListener('click', (e: any) => {
          e.stopPropagation();
        });
        searchInput.addEventListener('keydown', (e: any) => {
          e.stopPropagation();
        });
      }

      if (panel.firstChild) {
        panel.insertBefore(searchContainer, panel.firstChild);
      } else {
        panel.appendChild(searchContainer);
      }

      if (searchInput) {
        setTimeout(() => {
          searchInput.focus();
        }, 50);
      }
    }
  }

  filterUsers(event: any) {
    const searchTerm = event ? (event.target ? event.target.value : event) : this.userSearchValue;
    this.userSearchValue = searchTerm || '';

    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredCustomerList = this.customerList;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredCustomerList = this.customerList.filter((customer) => {
      const name = (customer.name || '').toLowerCase();
      const email = (customer.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }

  save() {
    const { notif } = defaults;

    if (this.myForm.invalid) {
      this.notifSer.error('Error', 'Please fill all required fields', notif);
      return;
    }

    this.spinnerService.show();
    const { txDate: selectedTxDate, ...rest } = this.myForm.getRawValue();
    const formData = {
      // Include disabled fields (everything but matchedTopupAmount/matchedStatus)
      // when updating an existing row - harmless, the update endpoint ignores them.
      ...rest,
      txDate: moment(selectedTxDate).toISOString(),
      _csrf: this.data._csrf,
    };

    if (this.isEditMode) {
      formData['_id'] = this.recordId;
      this.offrampService.updateOfframpCryptoReceived(formData).subscribe(
        (response: any) => {
          this.spinnerService.hide();
          if (response.status || response.success) {
            this.notifSer.success('Success', response.message || 'Record updated successfully', notif);
            this.dialogRef.close({ success: true, data: response.data });
          } else {
            this.notifSer.error('Error', response.message || 'Failed to update record', notif);
          }
        },
        (err) => {
          this.spinnerService.hide();
          this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to update record', notif);
        }
      );
    } else {
      this.offrampService.createOfframpCryptoReceived(formData).subscribe(
        (response: any) => {
          this.spinnerService.hide();
          if (response.status || response.success) {
            this.notifSer.success('Success', response.message || 'Record created successfully', notif);
            this.dialogRef.close({ success: true, data: response.data });
          } else {
            this.notifSer.error('Error', response.message || 'Failed to create record', notif);
          }
        },
        (err) => {
          this.spinnerService.hide();
          this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to create record', notif);
        }
      );
    }
  }

  onCancel() {
    this.dialogRef.close({ success: false });
  }
}



// WEBPACK FOOTER //
// ./src/app/offramp-crypto-received/add-offramp-crypto-received/add-offramp-crypto-received.component.ts